import { useEffect, useState } from 'react';

import { CHALLENGER_BREAKPOINTS } from '../media';

const isTextEditable = (target: EventTarget | null) => {
  if (!(target instanceof HTMLElement)) return false;
  if (target.isContentEditable) return true;
  if (target instanceof HTMLTextAreaElement) {
    return !target.disabled && !target.readOnly;
  }
  if (!(target instanceof HTMLInputElement)) return false;
  if (target.disabled || target.readOnly) return false;

  switch (target.type) {
    case 'button':
    case 'checkbox':
    case 'color':
    case 'file':
    case 'hidden':
    case 'image':
    case 'radio':
    case 'range':
    case 'reset':
    case 'submit':
      return false;
    default:
      return true;
  }
};

const viewportKeyboardInset = (viewport: VisualViewport) =>
  Math.max(
    0,
    Math.round(window.innerHeight - viewport.height - viewport.offsetTop),
  );

/**
 * Layout overlap (px) of the mobile virtual keyboard. Fixed bottom drawers
 * sit on the layout viewport, so they float above the keyboard unless they
 * are translated down by this inset (ENG-1125).
 */
export const useKeyboardInset = () => {
  const [inset, setInset] = useState(0);

  useEffect(() => {
    const viewport = window.visualViewport;

    const measure = () => {
      if (window.innerWidth >= CHALLENGER_BREAKPOINTS.tabletMinPx) {
        setInset((prev) => (prev === 0 ? prev : 0));
        return;
      }

      const editing = isTextEditable(document.activeElement);
      if (!editing) {
        setInset((prev) => (prev === 0 ? prev : 0));
        return;
      }

      if (!viewport) {
        const fallback = window.innerHeight;
        setInset((prev) => (prev === fallback ? prev : fallback));
        return;
      }

      const fromViewport = viewportKeyboardInset(viewport);
      // Android may resize the layout viewport with the keyboard so the
      // visualViewport delta is ~0. Still tuck the drawer away while editing.
      const next = fromViewport > 0 ? fromViewport : window.innerHeight;
      setInset((prev) => (prev === next ? prev : next));
    };

    measure();
    viewport?.addEventListener('resize', measure);
    viewport?.addEventListener('scroll', measure);
    window.addEventListener('resize', measure);
    document.addEventListener('focusin', measure);
    let focusOutFrame: number | null = null;
    const onFocusOut = () => {
      focusOutFrame = requestAnimationFrame(measure);
    };
    document.addEventListener('focusout', onFocusOut);

    return () => {
      if (focusOutFrame !== null) cancelAnimationFrame(focusOutFrame);
      viewport?.removeEventListener('resize', measure);
      viewport?.removeEventListener('scroll', measure);
      window.removeEventListener('resize', measure);
      document.removeEventListener('focusin', measure);
      document.removeEventListener('focusout', onFocusOut);
    };
  }, []);

  return inset;
};
